import { useParams } from "react-router-dom";
import { Card, Group, Stack, Text } from "@mantine/core";
import { PageHeader } from "@/components/PageHeader";
import { DetailGrid } from "@/components/DetailGrid";
import { JsonExpander } from "@/components/JsonExpander";
import { RelatedList } from "@/components/RelatedList";
import { StatusBadge } from "@/components/StatusBadge";
import { Mono } from "@/components/Mono";
import { EmptyState, ErrorState, LoadingState, NotProjectedState } from "@/components/states";
import { isPermissionError } from "@/lib/errors";
import { useFirestoreDoc } from "@/firestore/hooks";
import { isNotYetProjected } from "@/firestore/collections";
import type { WithDocId } from "@/firestore/types";
import { entityListPath } from "@/routes/paths";
import type { AnyEntityConfig } from "@/entities/types";

/** Generic detail view driven by an {@link EntityConfig}. */
export function EntityDetailPage({ config }: { config: AnyEntityConfig }) {
  const { id } = useParams<{ id: string }>();
  const { data: doc, loading, error } = useFirestoreDoc<Record<string, unknown>>(
    config.collection,
    id,
  );

  const crumbs = [{ label: config.pluralTitle, to: entityListPath(config.key) }];

  if (loading) {
    return (
      <>
        <PageHeader title={config.title} crumbs={crumbs} />
        <LoadingState />
      </>
    );
  }
  if (error) {
    return (
      <>
        <PageHeader title={config.title} crumbs={crumbs} />
        {isNotYetProjected(config.collection) && isPermissionError(error) ? (
          <NotProjectedState />
        ) : (
          <ErrorState error={error} />
        )}
      </>
    );
  }
  if (!doc) {
    return (
      <>
        <PageHeader title={config.title} crumbs={crumbs} />
        {isNotYetProjected(config.collection) ? (
          <NotProjectedState />
        ) : (
          <EmptyState title="Not found" description={`No ${config.title.toLowerCase()} with id ${id}.`} />
        )}
      </>
    );
  }

  const typed = doc as WithDocId<Record<string, unknown>>;
  const state = typeof typed.state === "string" ? typed.state : undefined;
  const related = config.related?.(typed) ?? [];

  return (
    <>
      <PageHeader
        title={
          <Group gap="sm">
            {config.title}
            {state ? <StatusBadge state={state} /> : null}
          </Group>
        }
        subtitle={config.subtitle ? config.subtitle(typed) : <Mono value={typed._docId} />}
        crumbs={crumbs}
        actions={config.actions?.(typed)}
      />

      <Stack gap="lg">
        <Card withBorder radius="md" padding="lg">
          <DetailGrid fields={config.detailFields(typed)} />
        </Card>

        {config.panels?.(typed)}

        {related.map((spec, i) => (
          <RelatedList key={i} spec={spec} />
        ))}

        <Card withBorder radius="md" padding="md">
          <Text fw={600} mb="xs">
            Raw document
          </Text>
          <JsonExpander value={config.redactRaw ? config.redactRaw(typed) : typed} />
        </Card>
      </Stack>
    </>
  );
}
